import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RegistrationService } from '../../services/registration.service';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';

type RemainingTime = { days: number; hours: number; minutes: number };

@Component({
  selector: 'app-partners',
  standalone: true,
  imports: [CommonModule, ConfirmDialogComponent],
  templateUrl: './partners.component.html',
  styleUrls: ['./partners.component.css']
})
export class PartnersComponent implements OnInit {

  partners: any[] = [];
  loading = false;
  error = '';
  updatingId: string | number | null = null;

  showConfirm = false;
  selectedPartner: any = null;

  constructor(private registrationService: RegistrationService) {}

  ngOnInit(): void {
    this.fetchPartners();
  }

  async fetchPartners() {
    this.loading = true;
    try {
      const res = await firstValueFrom(this.registrationService.list());
      this.partners = (res.records || []).filter((p: any) => p?.id);
    } catch {
      this.error = 'Failed to load partners';
    } finally {
      this.loading = false;
    }
  }

  // AUTO-EXPIRE + Remaining Time
  getRemainingTime(p: any): RemainingTime | null {
    if (!p?.validity || p.expired) return null;

    const base = new Date(p.updatedAt || p.registeredAt);
    const expiry = new Date(base);
    expiry.setDate(expiry.getDate() + p.validity);

    const diff = expiry.getTime() - Date.now();

    //  auto expire + DB update
    if (diff <= 0) {
      this.autoExpirePartner(p);
      return null;
    }

    const mins = Math.floor(diff / 60000);
    return {
      days: Math.floor(mins / 1440),
      hours: Math.floor((mins % 1440) / 60),
      minutes: mins % 60
    };
  }

  // AUTO EXPIRE (DB + UI)
  async autoExpirePartner(p: any) {
    if (p.expired || this.updatingId === p.id) return;

    this.updatingId = p.id;

    try {
      await firstValueFrom(
        this.registrationService.update(p.id.toString(), {
          expired: true,
          validity: 0
        })
      );

      p.expired = true;
      p.validity = 0;

    } catch (e) {
      console.error('Auto expire failed', e);
    } finally {
      this.updatingId = null;
    }
  }

  openConfirm(p: any) {
    this.selectedPartner = p;
    this.showConfirm = true;
  }

  onCancel() {
    this.showConfirm = false;
    this.selectedPartner = null;
  }

  async onConfirm() {
    if (!this.selectedPartner) return;

    const p = this.selectedPartner;
    this.updatingId = p.id;

    try {
      const updateData = p.expired
        ? { expired: false, validity: 365 }
        : { expired: true, validity: 0 };

      await firstValueFrom(
        this.registrationService.update(p.id.toString(), updateData)
      );

      p.expired = updateData.expired;
      p.validity = updateData.validity;

    } catch {
      alert('Failed to update status');
    } finally {
      this.updatingId = null;
      this.onCancel();
    }
  }
}
